import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TodoItem, Project } from "../../../lib/adhd-todo-manager";
import { ADHDAPIAdapter } from "../../../lib/adhd-api-adapter";
import { OverwhelmAssessment } from "../../../lib/overwhelm-detector";
import { EnergyLevel } from "../../../lib/energy-context-service";
import { CollapsibleProjectTree, CollapsibleProjectTreeRef } from "../CollapsibleProjectTree";
import { VisualCountdownTimer } from "../../timers/VisualCountdownTimer";
import { XPDisplay } from "../../gamification/XPDisplay";
import { StreakWidget } from "../../gamification/StreakWidget";
import { useXP } from "../../../hooks/use-xp";
import { XPRewardCalculator } from "../../../lib/xp-rewards";
import {
  FiCheckCircle,
  FiPlay,
  FiFolder,
  FiTarget,
  FiLoader,
  FiClock,
  FiStar,
} from "../../../lib/icons";
import { FocusModeToggle } from "../../focus-mode/FocusModeToggle";
import { TemplateSelector } from "../../templates/TemplateSelector";
import { TaskTemplate, convertTemplateToTodos } from "../../../lib/task-templates";

// Temporary stubs for missing components
const TaskCreationModal = (props: any) => null;
const TaskEditModal = (props: any) => null;

export interface ADHDAssistantContentProps {
  // Data props
  todos: TodoItem[];
  projects: Project[];
  bodyDoublingSession: {
    taskId: string;
    startTime: Date;
    duration: number;
    checkInInterval: number;
  } | null;
  isLoading: boolean;
  showCreateModal: boolean;
  editingTodo: TodoItem | null;
  overwhelmAssessment: OverwhelmAssessment | null;
  currentEnergyLevel: EnergyLevel;
  aiPersonalityMode: "supportive" | "focused" | "calm";

  // Action props
  setShowCreateModal: (show: boolean) => void;
  setEditingTodo: (todo: TodoItem | null) => void;
  startBodyDoubling: (todo: TodoItem) => void;
  checkForOverwhelm: (userMessage?: string) => void;

  // Refs
  projectTreeRef: React.RefObject<CollapsibleProjectTreeRef>;
  loadingRef: React.RefObject<boolean>;

  // Other props
  videoRef?: React.RefObject<HTMLVideoElement>;
  videoStream?: MediaStream | null;
  onVideoStreamChange?: (stream: MediaStream | null) => void;
  onNavigateToSettings?: () => void;

  // Services
  adhdAdapter: ADHDAPIAdapter;
  userTimezone: string;
}

export const ADHDAssistantContent: React.FC<ADHDAssistantContentProps> = ({
  // Data props
  todos,
  projects,
  bodyDoublingSession,
  isLoading,
  showCreateModal,
  editingTodo,
  overwhelmAssessment,
  currentEnergyLevel,
  aiPersonalityMode,

  // Action props
  setShowCreateModal,
  setEditingTodo,
  startBodyDoubling,
  checkForOverwhelm,

  // Refs
  projectTreeRef,
  loadingRef,

  // Other props
  videoRef,
  videoStream,
  onVideoStreamChange,
  onNavigateToSettings,

  // Services
  adhdAdapter,
  userTimezone,
}) => {
  const { t } = useTranslation(["adhd", "common"]);
  const { addXP } = useXP();
  const xpCalculator = useMemo(() => new XPRewardCalculator(), []);

  // Timer state for focus sessions
  const [showTimer, setShowTimer] = useState(false);
  const timerDuration = 25 * 60; // 25 minutes default (Pomodoro)

  // Template selector state
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const handleTimerComplete = useCallback(() => {
    // Timer completed - award XP and show notification
    const focusSessionReward = xpCalculator.calculateFocusSessionXP(timerDuration / 60, false);
    addXP(focusSessionReward.amount, focusSessionReward.reason);

    console.log(`Focus session completed! Earned ${focusSessionReward.amount} XP`);
    setShowTimer(false);

    // TODO: Add toast notification for session completion
    // TODO: Offer break suggestion
  }, [xpCalculator, timerDuration, addXP]);

  const handleStartFocusSession = useCallback(() => {
    setShowTimer(true);
  }, []);

  const handleEndFocusSession = useCallback(() => {
    setShowTimer(false);
  }, []);

  // Track task completion for XP rewards
  const previousTodos = useRef<TodoItem[]>([]);

  useEffect(() => {
    // Compare current todos with previous to detect completions
    if (previousTodos.current.length > 0) {
      const newlyCompleted = todos.filter(
        todo =>
          todo.status === "completed" &&
          previousTodos.current.find(prev => prev.id === todo.id && prev.status !== "completed")
      );

      // Award XP for newly completed tasks
      newlyCompleted.forEach(task => {
        const reward = xpCalculator.calculateTaskCompletionXP(task);
        addXP(reward.amount, reward.reason);
        console.log(`Task completed! Earned ${reward.amount} XP for "${task.title}"`);
      });
    }

    // Update reference for next comparison
    previousTodos.current = [...todos];
  }, [todos, xpCalculator, addXP]);

  // Handle moving todos to projects
  const handleMoveToProject = useCallback(
    async (todoId: string, projectId: string | null) => {
      try {
        const todo = todos.find(t => t.id === todoId);
        if (!todo) return;

        // Update via API if signed in
        await adhdAdapter.updateTodo({ todo_id: todoId, project_id: projectId });
      } catch (error) {
        console.error("Failed to move task:", error);
      }
    },
    [todos, adhdAdapter]
  );

  const handleCreateTodo = useCallback(
    async (todoData: any) => {
      try {
        const result = await adhdAdapter.createTodo(todoData);
        setShowCreateModal(false);
        return result;
      } catch (err) {
        throw err;
      }
    },
    [adhdAdapter, setShowCreateModal]
  );

  const handleUpdateTodo = useCallback(
    async (todoId: string, updates: any) => {
      try {
        const result = await adhdAdapter.updateTodo({ todo_id: todoId, ...updates });
        setEditingTodo(null);
        return result;
      } catch (err) {
        throw err;
      }
    },
    [adhdAdapter, setEditingTodo]
  );

  const handleSelectTemplate = useCallback(
    async (template: TaskTemplate, projectId?: string) => {
      try {
        // Convert template to todos
        const todoItems = convertTemplateToTodos(template, projectId);

        // Create todos via API
        const createdTodos = await Promise.all(
          todoItems.map(async todoData => {
            return await adhdAdapter.createTodo(todoData);
          })
        );

        // Award XP for using a template
        const templateXP = template.tasks.length * 25; // 25 XP per task in template
        addXP(templateXP, `Used template: ${template.name}`);

        console.log(
          `Template "${template.name}" applied! Created ${createdTodos.length} tasks and earned ${templateXP} XP`
        );

        // Close the template selector
        setShowTemplateSelector(false);
      } catch (error) {
        console.error("Failed to create tasks from template:", error);
        // TODO: Show error toast notification
      }
    },
    [adhdAdapter, addXP]
  );

  return (
    <div className="h-full flex flex-col bg-background text-foreground overflow-hidden adhd-assistant">
      <div className="assistant-header flex items-center justify-between p-4 border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <FiTarget className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">ADHD Assistant</h2>
        </div>

        <div className="essential-controls flex items-center gap-3">
          <StreakWidget size="small" showDetails={false} autoCheckIn={true} />
          <XPDisplay size="small" showLevel={true} showProgress={true} showXPText={false} />

          <div className="flex items-center gap-2">
            <FocusModeToggle
              size="small"
              showLabel={false}
              showKeyboardShortcut={false}
              className="bg-gray-600 text-white hover:bg-blue-600 transition-colors"
            />

            <button
              onClick={handleStartFocusSession}
              disabled={isLoading || showTimer}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Start focus session"
            >
              <FiClock className="w-4 h-4" />
              <span>Focus</span>
            </button>

            <button
              onClick={() => setShowTemplateSelector(true)}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Use task template"
            >
              <FiStar className="w-4 h-4" />
              <span>Templates</span>
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              disabled={isLoading}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={t("adhd:tasks.create_new_task")}
            >
              <FiCheckCircle className="w-4 h-4" />
              <span>{t("adhd:tasks.add_task")}</span>
            </button>
          </div>
        </div>

        {bodyDoublingSession && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-md text-sm">
            <FiPlay className="w-4 h-4" />
            <span>Body doubling active</span>
          </div>
        )}
      </div>

      {/* Focus Timer */}
      {showTimer && (
        <div className="px-4 py-3 border-b bg-green-50 dark:bg-green-900/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <VisualCountdownTimer
                duration={timerDuration}
                size="small"
                color="#059669"
                onComplete={handleTimerComplete}
                title="Focus Session"
                showControls={true}
              />
              <div className="text-sm text-green-700 dark:text-green-300">
                <p className="font-medium">Focus Mode Active</p>
                <p className="text-xs opacity-75">Stay focused on your current task</p>
              </div>
            </div>
            <button
              onClick={handleEndFocusSession}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              End Session
            </button>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className="conversation-container flex-1 overflow-hidden px-6 py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <FiLoader className="w-8 h-8 text-primary animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Loading your tasks...</h3>
            <p className="text-muted-foreground">Getting your productivity data ready</p>
          </div>
        ) : todos.length === 0 && projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 rounded-2xl bg-muted/50 flex items-center justify-center mb-6">
              <FiFolder className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">Ready to get organized?</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Start by creating your first task or project. I'm here to help you stay focused and
              productive.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <FiCheckCircle className="w-4 h-4" />
              Create your first task
            </button>
          </div>
        ) : (
          <div className="h-full overflow-auto">
            <CollapsibleProjectTree
              ref={projectTreeRef}
              projects={projects}
              todos={todos}
              onTodoUpdate={() => {}} // This will be handled by optimistic updates through the container
              onTodoEdit={setEditingTodo}
              onStartBodyDoubling={startBodyDoubling}
              onMoveToProject={handleMoveToProject}
            />
          </div>
        )}
      </div>

      {/* Task Creation Modal */}
      {showCreateModal && (
        <TaskCreationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateTodo={handleCreateTodo}
          projects={projects}
        />
      )}

      {/* Task Edit Modal */}
      {editingTodo && (
        <TaskEditModal
          isOpen={!!editingTodo}
          onClose={() => setEditingTodo(null)}
          todo={editingTodo}
          onUpdateTodo={handleUpdateTodo}
          projects={projects}
        />
      )}

      {/* Template Selector */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelectTemplate={handleSelectTemplate}
        projects={projects}
      />
    </div>
  );
};
